import React, { useState, useEffect, useCallback } from 'react';
import UploadImage from '@/components/UploadImage';
import { uploadResourcePositionImage } from '@/api/upload';
import { DeleteOutlined } from '@ant-design/icons';

export interface ProductImage {
  imageUrl: string;
  ratio: number;
}

export interface ImagesUploaderProps {
  value?: ProductImage[];
  onChange?: (images: ProductImage[]) => void;
  limit?: number;
  fileTypes?: string[];
  allowLottieSvga?: boolean;
  showDelete?: boolean;
}

const ImagesUploader: React.FC<ImagesUploaderProps> = ({
  value = [],
  onChange,
  limit = 5,
  fileTypes,
  allowLottieSvga = false,
  showDelete = false,
}) => {
  // 1. 本地状态完全接管图片列表（彻底隔离外部值的影响）
  const [imageList, setImageList] = useState<ProductImage[]>([]);

  // 2. 仅在初始挂载/外部value真的变化时同步（避免重复触发）
  useEffect(() => {
    const parsed = Array.isArray(value) 
      ? value.filter(item => item?.imageUrl) // 过滤无效项
      : typeof value === 'string'
        ? JSON.parse(value).filter((item: any) => item?.imageUrl)
        : [];
    setImageList(parsed);
  }, [JSON.stringify(value)]); // 深度监听value变化

  // 3. 计算图片比例（抽离为独立方法）
  const calculateRatio = useCallback(async (url: string): Promise<number> => {
    // 非图片文件直接返回1
    if (url.toLowerCase().endsWith('.svga') || 
        url.toLowerCase().endsWith('.json') || 
        url.toLowerCase().endsWith('.lottie')) {
      return 1;
    }

    try {
      const img = new Image();
      // 解决跨域图片无法获取宽高的问题（如果有）
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      return Number((img.width / img.height).toFixed(2));
    } catch (error) {
      console.warn('获取图片比例失败:', error);
      return 1;
    }
  }, []);

  // 4. 编辑单张图片（明确替换逻辑）
  const handleEditImage = useCallback(async (index: number, newUrl: string) => {
    if (!newUrl) return;
    const ratio = await calculateRatio(newUrl);
    const newList = [...imageList];
    // 仅替换指定索引的图片
    newList[index] = { imageUrl: newUrl, ratio };
    setImageList(newList);
    onChange?.(newList);
  }, [imageList, calculateRatio, onChange]);

  // 5. 新增图片（明确追加逻辑，绝对不替换）
  const handleAddImage = useCallback(async (urlOrUrls: string | string[]) => {
    if (!urlOrUrls) return;
    
    // 转为数组并去重（避免重复上传）
    const urls = Array.isArray(urlOrUrls) 
      ? urlOrUrls.filter(url => url && !imageList.some(item => item.imageUrl === url))
      : [urlOrUrls].filter(url => url && !imageList.some(item => item.imageUrl === url));
    
    if (urls.length === 0) return;

    // 计算剩余可上传数量
    const remaining = limit - imageList.length;
    if (remaining <= 0) return;

    // 只处理不超过限制的图片
    const uploadUrls = urls.slice(0, remaining);
    const newImages: ProductImage[] = [];

    // 批量计算比例
    for (const url of uploadUrls) {
      const ratio = await calculateRatio(url);
      newImages.push({ imageUrl: url, ratio });
    }

    // 追加到列表（核心：用concat而非直接push，避免引用问题）
    const newList = imageList.concat(newImages);
    setImageList(newList);
    onChange?.(newList);
  }, [imageList, limit, calculateRatio, onChange]);

  // 6. 删除图片
  const handleDeleteImage = useCallback((index: number) => {
    const newList = [...imageList];
    newList.splice(index, 1);
    setImageList(newList);
    onChange?.(newList);
  }, [imageList, onChange]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {/* 已上传图片：使用独立key+索引，确保渲染稳定 */}
      {imageList.map((image, index) => (
        <div 
          key={`image-${index}-${image.imageUrl}`} // 唯一key避免渲染复用
          style={{ width: 104, height: 104, position: 'relative' }}
        >
          <UploadImage
            // 强制传递当前图片URL，确保回显正确
            value={image.imageUrl}
            // 编辑模式：仅传递单张图片的URL，且明确索引
            onChange={(url) => handleEditImage(index, url as string)}
            width={104}
            height={104}
            uploadApi={uploadResourcePositionImage}
            showSizeHint={false}
            fileTypes={fileTypes}
            allowLottieSvga={allowLottieSvga}
          />
          {showDelete && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteImage(index);
              }}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#ff4d4f',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              <DeleteOutlined style={{ fontSize: 12 }} />
            </div>
          )}
        </div>
      ))}

      {/* 新增按钮：仅在未达限制时显示 */}
      {imageList.length < limit && (
        <div 
          key="add-image-btn" // 固定key避免和已上传图片冲突
          style={{ width: 104, height: 104 }}
        >
          <UploadImage
            // 新增模式不传递value（避免复用编辑逻辑）
            value={undefined}
            // 新增模式：仅调用追加方法
            onChange={(urls) => handleAddImage(urls as string | string[])}
            width={104}
            height={104}
            uploadApi={uploadResourcePositionImage}
            showSizeHint={false}
            fileTypes={fileTypes}
            allowLottieSvga={allowLottieSvga}
          />
        </div>
      )}
    </div>
  );
};

export default ImagesUploader;